import React, { useRef } from 'react';
import { Editor } from '@tiptap/react';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, Code, Heading2,
  List, ListOrdered, ListChecks, Quote, Code2, Image as ImageIcon,
  AlignLeft, AlignCenter, AlignRight, Highlighter,
  Undo, Redo, Sigma,
} from 'lucide-react';
import { cn } from 'lib/utils';
import { TOOLBAR_PRESETS, ToolbarButton } from '../config/presets';
import { EditorPreset } from '../types';
import { uploadImageToS3 } from '../adapters/s3UploadAdapter';
import { insertMathCommand } from '../extensions/types';

interface ToolbarProps {
  editor: Editor;
  preset: EditorPreset;
  /** Optional context passed to S3 uploads, e.g. `questions/<testId>`. */
  uploadContext?: string;
}

const Toolbar: React.FC<ToolbarProps> = ({ editor, preset, uploadContext }) => {
  const imageInputRef = useRef<HTMLInputElement>(null);
  const onPickImage = () => imageInputRef.current?.click();

  const onImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const { url, key } = await uploadImageToS3(file, uploadContext);
      // Use insertContent (not setImage) so we can attach the b2Key attribute
      // added by the SignedImage extension. setImage from the base Image
      // extension drops unknown attributes.
      editor
        .chain()
        .focus()
        .insertContent({
          type: 'image',
          attrs: { src: url, alt: file.name, b2Key: key },
        })
        .run();
    } catch {
      // useImageUpload's toast handles user-facing error
    }
  };

  const buttons = TOOLBAR_PRESETS[preset];

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-gray-200 dark:border-gray-700 px-2 py-1.5 bg-gray-50 dark:bg-gray-900 rounded-t-md">
      {buttons.map((btn, i) => (
        <ToolbarBtn
          key={`${btn.id}-${i}`}
          btn={btn}
          editor={editor}
          onPickImage={onPickImage}
        />
      ))}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onImageChange}
      />
    </div>
  );
};

interface BtnProps {
  btn: ToolbarButton;
  editor: Editor;
  onPickImage: () => void;
}

const ToolbarBtn: React.FC<BtnProps> = ({ btn, editor, onPickImage }) => {
  if (btn.id === 'divider') {
    return <span className="mx-1 w-px h-5 bg-gray-300 dark:bg-gray-600" aria-hidden />;
  }

  const base =
    'h-7 w-7 inline-flex items-center justify-center rounded text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed';
  const active =
    'bg-gray-900/10 text-gray-900 dark:bg-white/15 dark:text-white';

  // Helper to keep the JSX small
  const Btn = (props: {
    title: string;
    icon: React.ReactNode;
    isActive?: boolean;
    onClick: () => void;
    disabled?: boolean;
  }) => (
    <button
      type="button"
      title={props.title}
      onClick={props.onClick}
      disabled={props.disabled}
      className={cn(base, props.isActive && active)}
    >
      {props.icon}
    </button>
  );

  switch (btn.id) {
    case 'undo':
      return <Btn title="Undo" icon={<Undo className="h-4 w-4" />} onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} />;
    case 'redo':
      return <Btn title="Redo" icon={<Redo className="h-4 w-4" />} onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} />;
    case 'heading':
      return <Btn title="Heading" icon={<Heading2 className="h-4 w-4" />} isActive={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} />;
    case 'bold':
      return <Btn title="Bold" icon={<Bold className="h-4 w-4" />} isActive={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} />;
    case 'italic':
      return <Btn title="Italic" icon={<Italic className="h-4 w-4" />} isActive={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} />;
    case 'underline':
      return <Btn title="Underline" icon={<UnderlineIcon className="h-4 w-4" />} isActive={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} />;
    case 'strike':
      return <Btn title="Strikethrough" icon={<Strikethrough className="h-4 w-4" />} isActive={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} />;
    case 'code':
      return <Btn title="Inline code" icon={<Code className="h-4 w-4" />} isActive={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()} />;
    case 'highlight':
      return <Btn title="Highlight" icon={<Highlighter className="h-4 w-4" />} isActive={editor.isActive('highlight')} onClick={() => editor.chain().focus().toggleHighlight().run()} />;
    case 'bulletList':
      return <Btn title="Bullet list" icon={<List className="h-4 w-4" />} isActive={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} />;
    case 'orderedList':
      return <Btn title="Numbered list" icon={<ListOrdered className="h-4 w-4" />} isActive={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} />;
    case 'taskList':
      return <Btn title="To-do list" icon={<ListChecks className="h-4 w-4" />} isActive={editor.isActive('taskList')} onClick={() => editor.chain().focus().toggleTaskList().run()} />;
    case 'blockquote':
      return <Btn title="Quote" icon={<Quote className="h-4 w-4" />} isActive={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} />;
    case 'codeBlock':
      return <Btn title="Code block" icon={<Code2 className="h-4 w-4" />} isActive={editor.isActive('codeBlock')} onClick={() => editor.chain().focus().toggleCodeBlock().run()} />;
    case 'textAlignLeft':
      return <Btn title="Align left" icon={<AlignLeft className="h-4 w-4" />} isActive={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()} />;
    case 'textAlignCenter':
      return <Btn title="Align center" icon={<AlignCenter className="h-4 w-4" />} isActive={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()} />;
    case 'textAlignRight':
      return <Btn title="Align right" icon={<AlignRight className="h-4 w-4" />} isActive={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()} />;
    case 'image':
      return <Btn title="Insert image" icon={<ImageIcon className="h-4 w-4" />} onClick={onPickImage} />;
    case 'math':
      return <Btn title="Insert math (LaTeX)" icon={<Sigma className="h-4 w-4" />} onClick={() => insertMathCommand(editor)} />;
    default:
      return null;
  }
};

export default Toolbar;
